import { WifiOff, RefreshCw, CheckCircle2 } from 'lucide-react'
import { useOnlineStatus } from '../../shared/hooks/useOnlineStatus'
import { useSyncQueue } from '../../shared/hooks/useSyncQueue'

interface Props {
  tripId: string | undefined
}

/**
 * Compact status pill shown in the trip header.
 * Hidden when online and queue is empty — not distracting during normal use.
 */
export function OfflineIndicator({ tripId }: Props) {
  const online = useOnlineStatus()
  const { pendingCount, syncing, triggerSync } = useSyncQueue(tripId)

  if (online && pendingCount === 0 && !syncing) return null

  if (!online) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-yellow-100 text-yellow-700 text-xs font-medium">
        <WifiOff className="w-3.5 h-3.5" />
        Offline
        {pendingCount > 0 && (
          <span className="ml-0.5 bg-yellow-600 text-white rounded-full px-1.5 py-0 text-[10px] leading-4">
            {pendingCount}
          </span>
        )}
      </span>
    )
  }

  if (syncing) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
        Syncing…
      </span>
    )
  }

  if (pendingCount > 0) {
    return (
      <button
        onClick={triggerSync}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-orange-100 text-orange-700 text-xs font-medium hover:bg-orange-200 transition"
      >
        <RefreshCw className="w-3.5 h-3.5" />
        Sync {pendingCount} change{pendingCount > 1 ? 's' : ''}
      </button>
    )
  }

  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
      <CheckCircle2 className="w-3.5 h-3.5" />
      Synced
    </span>
  )
}
